import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import Coachmark from './tutorial/Coachmark';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import './Auth.css';
import './CharacterSelection.css';
import './Farming.css';
import AuthLayeredCard from './AuthLayeredCard';
import AuthSelectionToolbar from './AuthSelectionToolbar';
import { authButtonStyle, authStatPanelStyle, authCardStyle } from '../constants/authStyles';
import { usePopupMessage } from '../hooks/usePopupMessage';
import { useCharacterOptional } from '../context/CharacterContext';
import { useGameConfig } from '../context/GameConfigContext';
import { FARMING_MIN_LEVEL, FARM_EMPTY_SLOT_IMAGE, FARM_TILLED_SLOT_IMAGE } from '../constants/farming';
import { meetsMinLevel } from '../utils/levelRequirement';
import { getOnboardingPath } from '../utils/introVideo';
import { ProfessionBadge } from './ProfessionPanels';

const HARVEST_FLY_MS = 900;
const FARM_STATUS_SOFT_SYNC_MS = 30000;

const formatSeconds = (sec) => {
  const s = Math.max(0, Math.floor(sec));
  const h = Math.floor(s / 3600);
  const m = Math.floor(s % 3600 / 60);
  const r = s % 60;
  if (h > 0) {
    return `${h}:${String(m).padStart(2, '0')}:${String(r).padStart(2, '0')}`;
  }
  return `${m}:${String(r).padStart(2, '0')}`;
};

const growthStageFromPercent = (pct) => {
  if (pct >= 100) return 100;
  if (pct >= 80) return 80;
  if (pct >= 60) return 60;
  if (pct >= 40) return 40;
  return 20;
};

const liveAdjustSlot = (slot, serverNow, elapsedSinceFetch, maxWaterings) => {
  if (!slot || slot.state !== 'growing') return slot;

  const plantedAt = Number(slot.plantedAt) || 0;
  const growthSec = Number(slot.effectiveGrowthSec) || 0;
  let remainingSec = Number(slot.remainingSec) || 0;
  let growthPercent = Number(slot.growthPercent) || 0;
  let growthStage = Number(slot.growthStage) || 20;

  if (plantedAt > 0 && growthSec > 0) {
    const readyAt = plantedAt + growthSec;
    if (serverNow >= readyAt) {
      return {
        ...slot,
        state: 'ready',
        remainingSec: 0,
        growthPercent: 100,
        growthStage: 100,
        canWater: false,
        waterCooldownSec: 0,
      };
    }
    remainingSec = Math.max(0, readyAt - serverNow);
    const elapsed = Math.max(0, serverNow - plantedAt);
    growthPercent = Math.min(99, Math.floor((elapsed / growthSec) * 100));
    growthStage = growthStageFromPercent(growthPercent);
  } else {
    remainingSec = Math.max(0, remainingSec - elapsedSinceFetch);
    if (remainingSec <= 0) {
      return {
        ...slot,
        state: 'ready',
        remainingSec: 0,
        growthPercent: 100,
        growthStage: 100,
        canWater: false,
        waterCooldownSec: 0,
      };
    }
  }

  const waterCooldownSec = Math.max(0, (Number(slot.waterCooldownSec) || 0) - elapsedSinceFetch);
  const underWaterCap = (Number(slot.waterCount) || 0) < maxWaterings;
  return {
    ...slot,
    remainingSec,
    growthPercent,
    growthStage,
    waterCooldownSec,
    canWater: underWaterCap && waterCooldownSec <= 0,
  };
};

const slotEmoji = (state) => {
  if (state === 'ready') return '🌾';
  if (state === 'growing') return '🌱';
  if (state === 'tilled') return '🟤';
  return '🟫';
};

const getSlotGrowthImage = (cropConfig, slot) => {
  if (!cropConfig?.growthImages || !slot?.growthStage) return '';
  return cropConfig.growthImages[String(slot.growthStage)] || '';
};

const getOtherCatalogId = (item) => Number(item?.otherId ?? item?.itemId ?? 0);

const mergeInventoryOthers = (invItems, catalog) => {
  const byId = new Map((catalog || []).map((c) => [c.id, c]));
  return (invItems || []).map((item) => {
    const otherId = getOtherCatalogId(item);
    const meta = byId.get(otherId) || {};
    return {
      ...item,
      otherId,
      itemId: otherId,
      name: meta.name || `Item ${otherId}`,
      imageUrl: meta.imageUrl || '',
    };
  });
};

const Farming = () => {
  const navigate = useNavigate();
  const { t: ts } = useTranslation('selection');
  const { t: tc } = useTranslation('common');
  const username = localStorage.getItem('username');
  const { character, loading, hasCharacter, refreshCharacter } = useCharacterOptional() || {};
  const { setError, setStatus } = usePopupMessage();

  const [farmConfig, setFarmConfig] = useState(null);
  const [farmStatus, setFarmStatus] = useState(null);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [otherCatalog, setOtherCatalog] = useState([]);
  const [harvestFlyItems, setHarvestFlyItems] = useState([]);
  const [dropNotifications, setDropNotifications] = useState([]);
  const [activeField, setActiveField] = useState(0);
  const [dragOverSlot, setDragOverSlot] = useState(null);
  const [pickerModal, setPickerModal] = useState(null);
  const [tillModal, setTillModal] = useState(null);
  const [unlockModal, setUnlockModal] = useState(null);
  const [busy, setBusy] = useState(false);
  const [nowTick, setNowTick] = useState(0);
  const tickRef = useRef(null);
  const softSyncRef = useRef(null);
  const statusReceivedAtRef = useRef(Date.now());
  const draggedItemRef = useRef(null);
  const inventoryBtnRef = useRef(null);
  const slotRefs = useRef({});
  const harvestFlyTimeoutsRef = useRef({});
  const dropNotificationTimeoutsRef = useRef({});

  const level = character?.level ?? 1;
  const { gelistirmeConsole } = useGameConfig();
  const canFarm = meetsMinLevel(level, FARMING_MIN_LEVEL, gelistirmeConsole);

  const emptySlotImage = farmConfig?.emptySlotImage || FARM_EMPTY_SLOT_IMAGE;
  const tilledSlotImage = farmConfig?.tilledSlotImage || farmStatus?.tilledSlotImage || FARM_TILLED_SLOT_IMAGE;
  const farmingPoints = farmStatus?.farmingPoints ?? character?.farmingPoints ?? 0;
  const shovelEquipped = !!farmStatus?.shovelEquipped;
  const shovelEnhancement = farmStatus?.shovelEnhancement ?? 0;

  const cropConfigById = useMemo(() => {
    const map = new Map();
    (farmConfig?.crops || []).forEach((c) => map.set(c.id, c));
    return map;
  }, [farmConfig]);

  const seedOtherIds = useMemo(() => {
    const ids = new Set();
    (farmConfig?.crops || []).forEach((c) => {
      if (c.seedOtherItemId) ids.add(c.seedOtherItemId);
    });
    return ids;
  }, [farmConfig]);

  const cropOtherIds = useMemo(() => {
    const ids = new Set();
    (farmConfig?.crops || []).forEach((c) => {
      if (c.cropOtherItemId) ids.add(c.cropOtherItemId);
    });
    return ids;
  }, [farmConfig]);

  const waterOtherId = farmConfig?.waterOtherItemId ?? 0;
  const isSeedItem = useCallback((item) => seedOtherIds.has(getOtherCatalogId(item)), [seedOtherIds]);
  const isWaterItem = useCallback((item) => getOtherCatalogId(item) === waterOtherId, [waterOtherId]);
  const isCropItem = useCallback((item) => cropOtherIds.has(getOtherCatalogId(item)), [cropOtherIds]);

  const otherCatalogById = useMemo(() => {
    const map = new Map();
    (otherCatalog || []).forEach((entry) => map.set(entry.id, entry));
    return map;
  }, [otherCatalog]);

  const imageUrlForOtherId = useCallback((otherId) => {
    const meta = otherCatalogById.get(otherId);
    if (meta?.imageUrl) return `/${meta.imageUrl}`;
    return `/image/others/${otherId}.png`;
  }, [otherCatalogById]);

  const farmInventoryItems = useMemo(() => (
    inventoryItems.filter((item) => {
      const id = getOtherCatalogId(item);
      return (
        seedOtherIds.has(id) || id === waterOtherId || cropOtherIds.has(id)
      ) && (item.count ?? 0) > 0;
    })
  ), [inventoryItems, seedOtherIds, waterOtherId, cropOtherIds]);

  const seedInventoryItems = useMemo(() => (
    farmInventoryItems.filter((item) => isSeedItem(item))
  ), [farmInventoryItems, isSeedItem]);

  const waterInventoryItems = useMemo(() => (
    farmInventoryItems.filter((item) => isWaterItem(item))
  ), [farmInventoryItems, isWaterItem]);

  const fetchConfig = useCallback(async () => {
    const res = await axios.get('/api/farming/config');
    if (res.data?.success && res.data.config) {
      setFarmConfig(res.data.config);
    }
  }, []);

  const fetchStatus = useCallback(async () => {
    if (!username) return;
    const res = await axios.get(`/api/farming/status?username=${encodeURIComponent(username)}`);
    if (res.data?.success) {
      statusReceivedAtRef.current = Date.now();
      setFarmStatus(res.data);
    }
  }, [username]);

  const fetchInventory = useCallback(async () => {
    if (!username) return;
    const [invRes, catalogRes] = await Promise.all([
      axios.get(`/api/inventory?username=${encodeURIComponent(username)}&type=others`),
      axios.get('/api/items?type=others'),
    ]);
    if (invRes.data?.success && Array.isArray(invRes.data.items)) {
      const catalog = catalogRes.data?.success ? catalogRes.data.items : [];
      setOtherCatalog(catalog);
      setInventoryItems(mergeInventoryOthers(invRes.data.items, catalog));
    }
  }, [username]);

  useEffect(() => {
    const timeouts = harvestFlyTimeoutsRef.current;
    return () => {
      Object.values(timeouts).forEach((timeoutId) => clearTimeout(timeoutId));
    };
  }, []);

  useEffect(() => {
    const timeouts = dropNotificationTimeoutsRef.current;
    return () => {
      Object.values(timeouts).forEach((timeoutId) => clearTimeout(timeoutId));
    };
  }, []);

  const addDropNotification = useCallback((message) => {
    if (!message) return;
    const id = Date.now() + Math.random();
    setDropNotifications((prev) => {
      const next = [...prev, { id, message }];
      if (next.length <= 3) return next;
      const removed = next.shift();
      if (removed?.id && dropNotificationTimeoutsRef.current[removed.id]) {
        clearTimeout(dropNotificationTimeoutsRef.current[removed.id]);
        delete dropNotificationTimeoutsRef.current[removed.id];
      }
      return next;
    });
    const timeoutId = setTimeout(() => {
      setDropNotifications((prev) => prev.filter((n) => n.id !== id));
      delete dropNotificationTimeoutsRef.current[id];
    }, 3000);
    dropNotificationTimeoutsRef.current[id] = timeoutId;
  }, []);

  const spawnHarvestFlyAnimation = useCallback((fromRect, drops) => {
    const toEl = inventoryBtnRef.current;
    if (!fromRect || !toEl || !Array.isArray(drops) || drops.length === 0) return;

    const toRect = toEl.getBoundingClientRect();
    const baseStartX = fromRect.left + fromRect.width / 2;
    const baseStartY = fromRect.top + fromRect.height / 2;
    const endX = toRect.left + toRect.width / 2;
    const endY = toRect.top + toRect.height / 2;

    let flyIndex = 0;
    drops.forEach((drop) => {
      const otherId = Number(drop.otherItemId || drop.otherId || 0);
      const count = Math.max(1, Number(drop.count) || 1);
      if (!otherId) return;
      const flyCount = Math.min(count, 5);
      for (let i = 0; i < flyCount; i += 1) {
        const currentFly = flyIndex;
        flyIndex += 1;
        const delayMs = currentFly * 110;
        const spawn = () => {
          const id = `${Date.now()}-${currentFly}-${Math.random()}`;
          setHarvestFlyItems((prev) => [
            ...prev,
            {
              id,
              imageUrl: imageUrlForOtherId(otherId),
              startX: baseStartX + (currentFly % 3 - 1) * 12,
              startY: baseStartY + Math.floor(currentFly / 3) * 8,
              deltaX: endX - baseStartX,
              deltaY: endY - baseStartY,
            },
          ]);
          const timeoutId = setTimeout(() => {
            setHarvestFlyItems((prev) => prev.filter((item) => item.id !== id));
            delete harvestFlyTimeoutsRef.current[id];
          }, HARVEST_FLY_MS + 50);
          harvestFlyTimeoutsRef.current[id] = timeoutId;
        };
        if (delayMs > 0) {
          const delayId = setTimeout(spawn, delayMs);
          harvestFlyTimeoutsRef.current[`delay-${currentFly}`] = delayId;
        } else {
          spawn();
        }
      }
    });
  }, [imageUrlForOtherId]);

  const playHarvestFeedback = useCallback((data, fromRect) => {
    const drops = Array.isArray(data?.drops) ? data.drops.filter((d) => (d?.count || 0) > 0) : [];
    const cropCount = Number.isFinite(Number(data?.cropCount))
      ? Number(data.cropCount)
      : drops.filter((d) => d.kind !== 'seed').reduce((sum, d) => sum + (Number(d.count) || 0), 0);
    const seedCount = Number.isFinite(Number(data?.seedCount))
      ? Number(data.seedCount)
      : drops.filter((d) => d.kind === 'seed').reduce((sum, d) => sum + (Number(d.count) || 0), 0);

    if (drops.length > 0) {
      spawnHarvestFlyAnimation(fromRect, drops);
      // Aynı ürün satırlarını birleştirip göster (ör. ana hasat + ekstra bitki)
      const merged = new Map();
      drops.forEach((drop) => {
        const key = `${drop.kind || ''}-${drop.otherItemId || drop.name}`;
        const prev = merged.get(key);
        if (prev) {
          prev.count += Number(drop.count) || 0;
        } else {
          merged.set(key, {
            name: drop.name,
            count: Number(drop.count) || 0,
          });
        }
      });
      merged.forEach((entry) => {
        if (entry.name && entry.count > 0) {
          addDropNotification(ts('farmingDropNotify', { name: entry.name, count: entry.count }));
        }
      });
    } else {
      addDropNotification(ts('farmingHarvestNone', { crop: ts('farmingTitle') }));
    }

    if (data?.message) {
      setStatus(data.message);
    } else if (cropCount > 0 || seedCount > 0) {
      setStatus(`${cropCount} bitki, ${seedCount} tohum`);
    }
  }, [addDropNotification, spawnHarvestFlyAnimation, setStatus, ts]);

  useEffect(() => {
    if (!username) {
      navigate('/login');
      return;
    }
    if (!loading && !hasCharacter) {
      navigate(getOnboardingPath(username), { replace: true });
    }
  }, [username, navigate, loading, hasCharacter]);

  useEffect(() => {
    if (!canFarm || !username) return;
    fetchConfig().catch(() => setError(ts('farmingLoadFailed')));
    fetchStatus().catch(() => setError(ts('farmingLoadFailed')));
    fetchInventory().catch(() => {});
  }, [canFarm, username, fetchConfig, fetchStatus, fetchInventory, setError, ts]);

  useEffect(() => {
    if (!canFarm) return undefined;
    tickRef.current = setInterval(() => {
      setNowTick((n) => n + 1);
    }, 1000);
    softSyncRef.current = setInterval(() => {
      fetchStatus().catch(() => {});
    }, FARM_STATUS_SOFT_SYNC_MS);
    return () => {
      if (tickRef.current) clearInterval(tickRef.current);
      if (softSyncRef.current) clearInterval(softSyncRef.current);
    };
  }, [canFarm, fetchStatus]);

  const liveFields = useMemo(() => {
    void nowTick;
    const fields = farmStatus?.fields || [];
    const serverTime = Number(farmStatus?.serverTime) || 0;
    const elapsedSinceFetch = Math.max(0, Math.floor((Date.now() - statusReceivedAtRef.current) / 1000));
    const serverNow = serverTime > 0 ? serverTime + elapsedSinceFetch : Math.floor(Date.now() / 1000);
    const maxWaterings = farmConfig?.maxWaterings ?? 10;
    return fields.map((field) => ({
      ...field,
      slots: (field.slots || []).map((slot) => liveAdjustSlot(slot, serverNow, elapsedSinceFetch, maxWaterings)),
    }));
  }, [farmStatus, farmConfig, nowTick]);

  const postAction = async (url, body) => {
    if (busy) return null;
    setBusy(true);
    try {
      const res = await axios.post(url, { username, ...body });
      if (res.data?.success) {
        statusReceivedAtRef.current = Date.now();
        setFarmStatus(res.data);
        if (res.data.message) {
          setStatus(res.data.message);
        }
        await fetchInventory();
        return res.data;
      }
      setError(res.data?.message || ts('farmingActionFailed'));
      return null;
    } catch {
      setError(ts('farmingActionFailed'));
      return null;
    } finally {
      setBusy(false);
    }
  };

  const canDropOnSlot = (item, slot) => {
    if (!item || !slot) return false;
    if (slot.state === 'tilled' && isSeedItem(item)) return true;
    if (slot.state === 'growing' && slot.canWater && isWaterItem(item)) return true;
    return false;
  };

  const openPicker = async (type, slotIndex) => {
    await fetchInventory().catch(() => {});
    setPickerModal({ type, slotIndex });
  };

  const closePicker = () => setPickerModal(null);
  const closeTillModal = () => setTillModal(null);
  const closeUnlockModal = () => setUnlockModal(null);

  const handleFieldTabClick = (field, idx) => {
    if (!field.unlocked) {
      setActiveField(idx);
      setUnlockModal({ fieldIndex: idx });
      closePicker();
      return;
    }
    setActiveField(idx);
    closeUnlockModal();
    closePicker();
  };

  const handleUnlockField = async (fieldIndex, paymentMethod) => {
    const data = await postAction('/api/farming/unlock-field', { fieldIndex, paymentMethod });
    if (data) {
      closeUnlockModal();
      if (refreshCharacter) {
        await refreshCharacter().catch(() => {});
      }
    }
  };

  const tillSlot = async (slotIndex) => {
    return postAction('/api/farming/till', {
      fieldIndex: activeField,
      slotIndex,
    });
  };

  const plantSeed = async (slotIndex, userItemId) => {
    return postAction('/api/farming/plant', {
      fieldIndex: activeField,
      slotIndex,
      userItemId,
    });
  };

  const waterSlot = async (slotIndex) => {
    return postAction('/api/farming/water', {
      fieldIndex: activeField,
      slotIndex,
    });
  };

  const handleUseCrop = async (item) => {
    if (busy || !isCropItem(item)) return;
    setBusy(true);
    try {
      const res = await axios.post('/api/farming/use-crop', {
        username,
        userItemId: item.id,
      });
      if (!res.data?.success) {
        setError(res.data?.message || ts('farmingCropUseFailed'));
        return;
      }
      setStatus(res.data.message || ts('farmingCropUsed'));
      await fetchInventory();
      if (refreshCharacter) {
        await refreshCharacter().catch(() => {});
      }
    } catch (err) {
      setError(err.response?.data?.message || ts('farmingCropUseFailed'));
    } finally {
      setBusy(false);
    }
  };

  const handleSlotClick = async (slotIndex) => {
    const field = liveFields?.[activeField];
    const slot = field?.slots?.[slotIndex];
    if (!slot || !field?.unlocked) return;

    if (slot.state === 'ready') {
      const fromEl = slotRefs.current[slotIndex];
      const fromRect = fromEl ? fromEl.getBoundingClientRect() : null;
      const data = await postAction('/api/farming/harvest', { fieldIndex: activeField, slotIndex });
      if (data) {
        playHarvestFeedback(data, fromRect);
      }
      return;
    }
    if (slot.state === 'growing' && slot.canWater) {
      await openPicker('water', slotIndex);
      return;
    }
    if (slot.state === 'tilled') {
      await openPicker('seed', slotIndex);
      return;
    }
    if (slot.state === 'empty') {
      if (!shovelEquipped) {
        setError(ts('farmingNoShovel'));
        return;
      }
      setTillModal({ slotIndex });
    }
  };

  const handleTillConfirm = async () => {
    if (!tillModal || busy) return;
    const data = await tillSlot(tillModal.slotIndex);
    if (data) closeTillModal();
  };

  const handlePickerSelect = async (item) => {
    if (!pickerModal || busy) return;
    if (pickerModal.type === 'seed') {
      if (!isSeedItem(item)) {
        setError(ts('farmingNotSeed'));
        return;
      }
      const data = await plantSeed(pickerModal.slotIndex, item.id);
      if (data) closePicker();
      return;
    }
    if (pickerModal.type === 'water') {
      const data = await waterSlot(pickerModal.slotIndex);
      if (data) closePicker();
    }
  };

  const handleInvDragStart = (event, item) => {
    if (busy || (!isSeedItem(item) && !isWaterItem(item))) {
      event.preventDefault();
      return;
    }
    draggedItemRef.current = item;
    const payload = JSON.stringify({ id: item.id, otherId: getOtherCatalogId(item) });
    event.dataTransfer.setData('application/json', payload);
    event.dataTransfer.setData('text/plain', payload);
    event.dataTransfer.effectAllowed = 'move';
  };

  const handleInvDragEnd = () => {
    draggedItemRef.current = null;
    setDragOverSlot(null);
  };

  const getDraggedItem = (event) => {
    if (draggedItemRef.current) return draggedItemRef.current;
    try {
      const raw = event.dataTransfer.getData('application/json')
        || event.dataTransfer.getData('text/plain');
      if (!raw) return null;
      const payload = JSON.parse(raw);
      return inventoryItems.find((inv) => inv.id === payload.id) || null;
    } catch {
      return null;
    }
  };

  const handleSlotDragEnter = (event, slotIndex) => {
    event.preventDefault();
    const slot = liveFields?.[activeField]?.slots?.[slotIndex];
    const item = draggedItemRef.current;
    if (item && canDropOnSlot(item, slot)) {
      setDragOverSlot(slotIndex);
    }
  };

  const handleSlotDragOver = (event, slotIndex) => {
    event.preventDefault();
    const slot = liveFields?.[activeField]?.slots?.[slotIndex];
    const item = draggedItemRef.current;
    if (item && canDropOnSlot(item, slot)) {
      event.dataTransfer.dropEffect = 'move';
      setDragOverSlot(slotIndex);
    } else {
      event.dataTransfer.dropEffect = 'none';
    }
  };

  const handleSlotDragLeave = (event) => {
    if (!event.currentTarget.contains(event.relatedTarget)) {
      setDragOverSlot(null);
    }
  };

  const handleSlotDrop = async (event, slotIndex) => {
    event.preventDefault();
    event.stopPropagation();
    setDragOverSlot(null);
    if (busy) return;

    const item = getDraggedItem(event);
    draggedItemRef.current = null;
    const slot = liveFields?.[activeField]?.slots?.[slotIndex];
    if (!item || !slot || !canDropOnSlot(item, slot)) return;

    if (slot.state === 'tilled' && isSeedItem(item)) {
      await plantSeed(slotIndex, item.id);
      return;
    }
    if (slot.state === 'growing' && slot.canWater && isWaterItem(item)) {
      await waterSlot(slotIndex);
    }
  };

  const activeFieldData = liveFields?.[activeField];
  const unlockFieldData = unlockModal != null
    ? liveFields?.[unlockModal.fieldIndex]
    : null;
  const playerGold = farmStatus?.gold ?? character?.gold ?? 0;
  const playerRuhParasi = farmStatus?.ruhParasi ?? character?.ruhParasi ?? 0;
  const pickerItems = pickerModal?.type === 'water' ? waterInventoryItems : seedInventoryItems;
  const pickerTitle = pickerModal?.type === 'water'
    ? ts('farmingPickWaterTitle')
    : ts('farmingPickSeedTitle');
  const pickerEmptyText = pickerModal?.type === 'water'
    ? ts('farmingPickWaterEmpty')
    : ts('farmingPickSeedEmpty');

  if (!canFarm) {
    return (
      <div className="auth-container auth-container--selection farming-page">
      <Coachmark
        tourId="farming"
        steps={[
          { selector: '.farming-grid', textKey: 'farmingGrid' },
      ]}
      />
        <AuthLayeredCard>
          <AuthSelectionToolbar
            onBack={() => navigate('/work')}
            backLabel={tc('back')}
            buttonStyle={authButtonStyle}
            title={ts('farmingTitle')}
            subtitle={gelistirmeConsole
              ? ts('workDevModeUnlocked')
              : ts('workFarmingLocked', { level: FARMING_MIN_LEVEL })}
          />
        </AuthLayeredCard>
      </div>
    );
  }

  return (
    <div className="auth-container auth-container--selection farming-page">
      <Coachmark
        tourId="farming"
        steps={[
          { selector: '.farming-grid', textKey: 'farmingGrid' },
      ]}
      />
      {harvestFlyItems.map((item) => (
        <img
          key={item.id}
          className="farming-harvest-fly"
          src={item.imageUrl}
          alt=""
          aria-hidden="true"
          style={{
            '--fly-start-x': `${item.startX}px`,
            '--fly-start-y': `${item.startY}px`,
            '--fly-delta-x': `${item.deltaX}px`,
            '--fly-delta-y': `${item.deltaY}px`,
          }}
        />
      ))}
      {dropNotifications.length > 0 && (
        <div className="drop-notifications-container farming-drop-notifications">
          {dropNotifications.map((notification) => (
            <div key={notification.id} className="drop-notification">
              {notification.message}
            </div>
          ))}
        </div>
      )}
      <AuthLayeredCard className="farming-card">
        <AuthSelectionToolbar
          className="farming-toolbar"
          onBack={() => navigate('/work')}
          backLabel={tc('back')}
          buttonStyle={authButtonStyle}
          title={`${ts('farmingTitle')} - ${ts('farmingSubtitle')}`}
          leadingExtra={(
            <button
              ref={inventoryBtnRef}
              type="button"
              className="auth-button auth-button--compact"
              style={authButtonStyle}
              onClick={() => navigate('/inventory', { state: { from: '/farming' } })}
            >
              {ts('menuInventory')}
            </button>
          )}
        />
        <ProfessionBadge skill="farming" />

        <div className="auth-selection-body farming-body">
          <div className="farming-balances">
            <span className="farming-gold">
              {ts('farmingGold', { amount: playerGold.toLocaleString() })}
            </span>
            <span className="farming-ruh">
              {ts('farmingRuhParasi', { amount: playerRuhParasi.toLocaleString() })}
            </span>
          </div>

          <div className="farming-stats-row">
            <div className="farming-stat-panel" style={authStatPanelStyle}>
              <span className="farming-stat-label">{ts('farmingShovelLabel')}</span>
              <span className="farming-stat-value">
                {shovelEquipped ? `+${shovelEnhancement}` : ts('farmingShovelNotEquipped')}
              </span>
            </div>
            <div className="farming-stat-panel" style={authStatPanelStyle}>
              <span className="farming-stat-label">{ts('farmingPointsLabel')}</span>
              <span className="farming-stat-value">{farmingPoints}</span>
            </div>
          </div>

          <div className="farming-field-tabs">
            {(liveFields || []).map((field, idx) => (
              <button
                key={field.index ?? idx}
                type="button"
                className={`farming-field-tab${activeField === idx ? ' farming-field-tab--active' : ''}${!field.unlocked ? ' farming-field-tab--locked' : ''}`}
                onClick={() => handleFieldTabClick(field, idx)}
              >
                {ts('farmingFieldTab', { n: idx + 1 })}
                {!field.unlocked ? ' 🔒' : ''}
              </button>
            ))}
          </div>

          {activeFieldData?.unlocked ? (
            <>
              <div
                className="farming-grid"
                style={{
                  gridTemplateColumns: `repeat(${activeFieldData.cols || 3}, 1fr)`,
                  maxWidth: `${Math.min(420, (activeFieldData.cols || 3) * 140)}px`,
                }}
              >
                {(activeFieldData.slots || []).map((slot, slotIndex) => {
                  const cropConfig = slot.cropId ? cropConfigById.get(slot.cropId) : null;
                  const growthImage = (slot.state === 'growing' || slot.state === 'ready')
                    ? getSlotGrowthImage(cropConfig, slot)
                    : '';
                  const isEmpty = slot.state === 'empty' || !slot.state;
                  const isTilled = slot.state === 'tilled';
                  return (
                  <div
                    key={slotIndex}
                    className="farming-slot-wrap"
                  >
                    <div
                      ref={(el) => { slotRefs.current[slotIndex] = el; }}
                      role="button"
                      tabIndex={0}
                      className={`farming-slot farming-slot--${slot.state || 'empty'}${dragOverSlot === slotIndex ? ' farming-slot--drop-target' : ''}${slot.state === 'ready' || isEmpty || isTilled || (slot.state === 'growing' && slot.canWater) ? ' farming-slot--clickable' : ''}`}
                      onClick={() => handleSlotClick(slotIndex)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') handleSlotClick(slotIndex);
                      }}
                      onDragEnter={(e) => handleSlotDragEnter(e, slotIndex)}
                      onDragOver={(e) => handleSlotDragOver(e, slotIndex)}
                      onDragLeave={handleSlotDragLeave}
                      onDrop={(e) => handleSlotDrop(e, slotIndex)}
                    >
                      {isEmpty ? (
                        <img
                          className="farming-slot-empty-img"
                          src={`/${emptySlotImage}`}
                          alt=""
                          draggable={false}
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      ) : isTilled ? (
                        <img
                          className="farming-slot-tilled-img"
                          src={`/${tilledSlotImage}`}
                          alt=""
                          draggable={false}
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      ) : growthImage ? (
                        <img
                          className="farming-slot-plant-img"
                          src={`/${growthImage}`}
                          alt={slot.cropName || ''}
                          draggable={false}
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      ) : (
                        <span className="farming-slot-icon">{slotEmoji(slot.state)}</span>
                      )}
                    </div>
                    {isEmpty && (
                      <span className="farming-slot-caption">{ts('farmingSlotEmpty')}</span>
                    )}
                    {isTilled && (
                      <span className="farming-slot-caption farming-slot-caption--tilled">{ts('farmingSlotTilled')}</span>
                    )}
                    {slot.state === 'growing' && (
                      <span className="farming-slot-caption farming-slot-caption--growing">
                        <span className="farming-slot-label">
                          {slot.cropName || ts('farmingGrowing')}
                          {slot.growthStage > 0 ? ` · %${slot.growthStage}` : ''}
                        </span>
                        <span className="farming-slot-label farming-slot-label--meta">
                          {slot.remainingSec > 0 ? `${formatSeconds(slot.remainingSec)} · ` : ''}
                          💧 {slot.waterCount || 0}
                          {slot.canWater ? ` · ${ts('farmingCanWater')}` : slot.waterCooldownSec > 0 ? ` · ${formatSeconds(slot.waterCooldownSec)}` : ''}
                        </span>
                      </span>
                    )}
                    {slot.state === 'ready' && (
                      <span className="farming-slot-caption farming-slot-caption--ready">
                        <span className="farming-slot-label">{slot.cropName || ts('farmingReady')}</span>
                        <span className="farming-slot-label">{ts('farmingTapHarvest')}</span>
                      </span>
                    )}
                  </div>
                  );
                })}
              </div>
            </>
          ) : (
            <p className="farming-field-locked">{ts('farmingFieldLocked')}</p>
          )}

          <div className="farming-inventory">
            <div className="farming-inventory-title">{ts('farmingInventoryTitle')}</div>
            {farmInventoryItems.length === 0 ? (
              <div className="farming-empty-inv">{ts('farmingNoItems')}</div>
            ) : (
              <div className="farming-inventory-grid">
                {farmInventoryItems.map((item) => {
                  const draggable = !busy && (isSeedItem(item) || isWaterItem(item));
                  const isCrop = isCropItem(item);
                  return (
                    <div
                      key={item.id}
                      className={`farming-inv-item${draggable ? ' farming-inv-item--draggable' : ''}${isCrop ? ' farming-inv-item--crop' : ''}`}
                      draggable={draggable}
                      onDragStart={(e) => handleInvDragStart(e, item)}
                      onDragEnd={handleInvDragEnd}
                      onClick={() => isCrop && handleUseCrop(item)}
                      onKeyDown={(e) => {
                        if (isCrop && (e.key === 'Enter' || e.key === ' ')) {
                          e.preventDefault();
                          handleUseCrop(item);
                        }
                      }}
                      role={isCrop ? 'button' : undefined}
                      tabIndex={isCrop ? 0 : undefined}
                      title={isCrop ? `${item.name} — ${ts('farmingUseCrop')}` : item.name}
                    >
                      <img
                        src={`/${item.imageUrl || `image/others/${getOtherCatalogId(item)}.png`}`}
                        alt=""
                        draggable={false}
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                      <div className="farming-inv-item-count">×{item.count ?? 1}</div>
                      <div className="farming-inv-item-name">{item.name}</div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {(character?.plantFoodBuffs?.length > 0) && (
            <div style={authStatPanelStyle}>
              <strong>{ts('plantFoodBuffTitle')}</strong>
              <ul style={{ margin: '0.35rem 0 0', paddingLeft: '1.1rem', fontSize: '0.85rem' }}>
                {character.plantFoodBuffs.map((b) => (
                  <li key={b.cropId}>
                    {b.cropName}: {b.effectSummary} ({ts('plantFoodBuffRemaining', { sec: b.remainingSec })})
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </AuthLayeredCard>

      {unlockModal && unlockFieldData && (
        <div className="farming-picker-overlay" onClick={closeUnlockModal}>
          <div
            className="farming-picker-content farming-unlock-content auth-card"
            style={authCardStyle}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="farming-picker-header">
              <h3>{ts('farmingUnlockTitle', { n: unlockModal.fieldIndex + 1 })}</h3>
              <p className="farming-unlock-desc">
                {ts('farmingUnlockDesc', { n: unlockModal.fieldIndex + 1 })}
              </p>
            </div>
            <div className="farming-unlock-balances">
              <span>{ts('farmingGold', { amount: playerGold.toLocaleString() })}</span>
              <span>{ts('farmingRuhParasi', { amount: playerRuhParasi.toLocaleString() })}</span>
            </div>
            <div className="farming-unlock-options">
              <button
                type="button"
                className="auth-button farming-unlock-option"
                style={authButtonStyle}
                disabled={busy || playerGold < (unlockFieldData.unlockGold || 0)}
                onClick={() => handleUnlockField(unlockModal.fieldIndex, 'gold')}
              >
                {ts('farmingUnlockGold', { cost: (unlockFieldData.unlockGold || 0).toLocaleString() })}
              </button>
              <button
                type="button"
                className="auth-button farming-unlock-option"
                style={authButtonStyle}
                disabled={busy || playerRuhParasi < (unlockFieldData.unlockRuhParasi || 0)}
                onClick={() => handleUnlockField(unlockModal.fieldIndex, 'ruhParasi')}
              >
                {ts('farmingUnlockRuh', { cost: (unlockFieldData.unlockRuhParasi || 0).toLocaleString() })}
              </button>
            </div>
            <div className="farming-picker-footer">
              <button
                type="button"
                className="auth-button"
                style={authButtonStyle}
                onClick={closeUnlockModal}
              >
                {tc('cancel')}
              </button>
            </div>
          </div>
        </div>
      )}

      {tillModal && (
        <div className="farming-picker-overlay" onClick={closeTillModal}>
          <div
            className="farming-picker-content auth-card"
            style={authCardStyle}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="farming-picker-header">
              <h3>{ts('farmingTillTitle')}</h3>
              <p className="farming-till-desc">
                {ts('farmingTillDesc', { level: shovelEnhancement })}
              </p>
            </div>
            <div className="farming-picker-footer farming-till-actions">
              <button
                type="button"
                className="auth-button"
                style={authButtonStyle}
                disabled={busy}
                onClick={handleTillConfirm}
              >
                {ts('farmingTillConfirm')}
              </button>
              <button
                type="button"
                className="auth-button"
                style={authButtonStyle}
                onClick={closeTillModal}
              >
                {tc('cancel')}
              </button>
            </div>
          </div>
        </div>
      )}

      {pickerModal && (
        <div className="farming-picker-overlay" onClick={closePicker}>
          <div
            className="farming-picker-content auth-card"
            style={authCardStyle}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="farming-picker-header">
              <h3>{pickerTitle}</h3>
            </div>
            <div className="farming-picker-body">
              {pickerItems.length === 0 ? (
                <p className="farming-picker-empty">{pickerEmptyText}</p>
              ) : (
                <div className="farming-picker-list">
                  {pickerItems.map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      className="farming-picker-item"
                      disabled={busy}
                      onClick={() => handlePickerSelect(item)}
                    >
                      <img
                        src={`/${item.imageUrl || `image/others/${getOtherCatalogId(item)}.png`}`}
                        alt=""
                        draggable={false}
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                      <div className="farming-picker-item-info">
                        <span className="farming-picker-item-name">{item.name}</span>
                        <span className="farming-picker-item-count">×{item.count ?? 1}</span>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="farming-picker-footer">
              <button
                type="button"
                className="auth-button"
                style={authButtonStyle}
                onClick={closePicker}
              >
                {tc('cancel')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Farming;
